import {
  IPartProTradeBidLot,
  IPartProTradeBidVehicleDetail,
  PartProTradeRejectReason,
  PartProTradeRejectReasonCode,
  PartProTradeVehicleStatus,
} from "../models/partPro/IPartProTradeBids";
import { normaliseCancelNote } from "./partProTradeCancel";

// Rejecting a whole PartPro Trade vehicle after bidding - it didn't turn up, or wasn't as described.
// The server is the authority; these keep the screen in step with it.

export const PARTPRO_TRADE_REJECT_NOTE_MAX_LENGTH = 500;

// Source strings, so they double as i18next keys
export const partProTradeRejectReasonLabels: Record<PartProTradeRejectReason, string> = {
  [PartProTradeRejectReason.NotArrived]: "Didn't arrive",
  [PartProTradeRejectReason.NotAsDescribed]: "Not as described",
  [PartProTradeRejectReason.Other]: "Other",
};

const codeLabels: Record<PartProTradeRejectReasonCode, string> = {
  NOT_ARRIVED: "Didn't arrive",
  NOT_AS_DESCRIBED: "Not as described",
  OTHER: "Other",
};

/** Withdrawn and rejected cars are already off Trade; drafts were never bid on. */
export function canRejectTradeVehicle(status: PartProTradeVehicleStatus | null | undefined): boolean {
  return status === "OPEN" || status === "CLOSED" || status === "COMPLETED";
}

/**
 * A reject needs a reason, and "Other" says nothing on its own to the buyers losing their bids,
 * so it needs a note too. Mirrors the API's check.
 */
export function isValidReject(reason: PartProTradeRejectReason | null | undefined, note: string | null | undefined): boolean {
  if (reason === null || reason === undefined || !(reason in PartProTradeRejectReason)) return false;
  return reason !== PartProTradeRejectReason.Other || normaliseCancelNote(note) !== undefined;
}

/** "Didn't arrive - no show on the day", or just the reason without a note. */
export function describeVehicleRejection(
  vehicle: Pick<IPartProTradeBidVehicleDetail, "rejectReason" | "rejectNote"> | null | undefined
): string {
  const label = vehicle?.rejectReason ? codeLabels[vehicle.rejectReason] ?? "Other" : "Rejected";
  const note = vehicle?.rejectNote?.trim();
  return note ? `${label} - ${note}` : label;
}

/** The accepted bids a reject will cancel - the ones the yard is about to let down. */
export function awardsCancelledByReject(vehicle: IPartProTradeBidVehicleDetail | null | undefined): IPartProTradeBidLot[] {
  return (vehicle?.lots ?? []).filter((lot) => lot.status === "AWARDED" && !!lot.award);
}
